/**
 * Configuration file for Harary's Generalized Tic-Tac-Toe target patterns
 * Each pattern defines:
 * - id: unique identifier for the pattern
 * - name: display name
 * - description: human-readable description
 * - icon: visual representation
 * - minBoardSize: minimum board size required for this pattern
 * - checkFunction: function that validates if a player has won with this pattern
 * 
 * All patterns work in all 4 rotations (0°, 90°, 180°, 270°)
 */

/**
 * Helper function to check if a pattern exists at a specific position and rotation
 * @param {Array} board - The game board
 * @param {Array} pattern - Array of relative positions for the pattern
 * @param {number} startRow - Starting row position
 * @param {number} startCol - Starting column position
 * @param {string} player - The player to check
 * @param {number} boardSize - The size of the game board
 * @returns {Object|null} Object with positions if pattern exists, null otherwise
 */
const checkPatternAtPosition = (board, pattern, startRow, startCol, player, boardSize) => {
  const positions = [];
  for (const [dr, dc] of pattern) {
    const row = startRow + dr;
    const col = startCol + dc;
    
    // Check bounds
    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) {
      return null;
    }
    
    // Check if position contains player's piece
    if (board[row][col] !== player) {
      return null;
    }
    
    positions.push({ row, col });
  }
  return { positions };
};

/**
 * Helper function to generate all transformations of a pattern (rotations + reflections)
 * @param {Array} basePattern - Base pattern positions
 * @param {boolean} isLinePattern - Whether this is a line pattern (tic/skinny)
 * @param {number} boardSize - The size of the game board
 * @param {string} patternId - The pattern identifier
 * @returns {Array} Array of all transformed patterns
 */
export const generateAllTransformations = (basePattern, isLinePattern = false, boardSize = 6, patternId = '') => {
  const transformations = [];
  
  // For line patterns, only generate horizontal and vertical orientations (no diagonals)
  if (isLinePattern) {
    // Horizontal orientation (original)
    transformations.push([...basePattern]);
    
    // Vertical orientation (rotated 90°)
    const vertical = basePattern.map(([dr, dc]) => [dc, 0]);
    transformations.push(vertical);
    
    // Note: Diagonal transformations removed for all line patterns
  } else {
    // For non-line patterns, generate all 4 rotations (0°, 90°, 180°, 270°)
    const rotations = [
      basePattern, // 0° rotation (original)
      basePattern.map(([dr, dc]) => [-dc, dr]), // 90° rotation
      basePattern.map(([dr, dc]) => [-dr, -dc]), // 180° rotation
      basePattern.map(([dr, dc]) => [dc, -dr]), // 270° rotation
    ];
    
    // Add each rotation
    transformations.push(...rotations);
    
    // Generate reflections for each rotation
    for (const rotation of rotations) {
      // Horizontal reflection (flip vertically)
      const horizontalReflection = rotation.map(([dr, dc]) => [-dr, dc]);
      transformations.push(horizontalReflection);
      
      // Vertical reflection (flip horizontally)
      const verticalReflection = rotation.map(([dr, dc]) => [dr, -dc]);
      transformations.push(verticalReflection);
    }
  }
  
  // Remove duplicates by normalizing and comparing patterns
  const uniqueTransformations = [];
  const seen = new Set();
  
  for (const transformation of transformations) {
    // Normalize the pattern by finding bounds and shifting to origin
    let minRow = 0, maxRow = 0, minCol = 0, maxCol = 0;
    for (const [dr, dc] of transformation) {
      minRow = Math.min(minRow, dr);
      maxRow = Math.max(maxRow, dr);
      minCol = Math.min(minCol, dc);
      maxCol = Math.max(maxCol, dc);
    }
    
    // Shift to origin and sort for consistent comparison
    const normalized = transformation
      .map(([dr, dc]) => [dr - minRow, dc - minCol])
      .sort((a, b) => {
        if (a[0] !== b[0]) return a[0] - b[0];
        return a[1] - b[1];
      });
    
    const key = normalized.map(([dr, dc]) => `${dr},${dc}`).join('|');
    if (!seen.has(key)) {
      seen.add(key);
      uniqueTransformations.push(transformation);
    }
  }
  
  return uniqueTransformations;
};

/**
 * Helper function to check a pattern in all transformations (rotations + reflections)
 * @param {Array} board - The game board
 * @param {Array} basePattern - Base pattern positions
 * @param {string} player - The player to check
 * @param {number} boardSize - The size of the game board
 * @param {boolean} isLinePattern - Whether this is a line pattern (tic/skinny)
 * @returns {Object|null} Object with winning positions if pattern exists, null otherwise
 */
const checkPatternAllTransformations = (board, basePattern, player, boardSize, isLinePattern = false, patternId = '') => {
  const transformations = generateAllTransformations(basePattern, isLinePattern, boardSize, patternId);
  
  // Check each transformation
  for (let i = 0; i < transformations.length; i++) {
    const transformation = transformations[i];
    // Find the bounds for this transformation
    let minRow = 0, maxRow = 0, minCol = 0, maxCol = 0;
    for (const [dr, dc] of transformation) {
      minRow = Math.min(minRow, dr);
      maxRow = Math.max(maxRow, dr);
      minCol = Math.min(minCol, dc);
      maxCol = Math.max(maxCol, dc);
    }

    

    
    // Check all possible starting positions for this transformation
    // For transformations with negative coordinates, we need to start at a position that ensures all coordinates are valid
    const minStartRow = Math.max(0, -minRow);
    const maxStartRow = Math.min(boardSize - 1, boardSize - 1 - maxRow);
    const minStartCol = Math.max(0, -minCol);
    const maxStartCol = Math.min(boardSize - 1, boardSize - 1 - maxCol);

    for (let startRow = minStartRow; startRow <= maxStartRow; startRow++) {
      for (let startCol = minStartCol; startCol <= maxStartCol; startCol++) {
        
        const result = checkPatternAtPosition(board, transformation, startRow, startCol, player, boardSize);
        if (result) {
          return result;
        }
      }
    }
  }
  
  return null;
};

/**
 * Specialized win check for tic pattern (3-in-a-row)
 * @param {Array} board - The game board
 * @param {string} player - The player to check ('X' or 'O')
 * @param {number} boardSize - The size of the game board
 * @returns {Object|null} Object with winning positions if player has won, null otherwise
 */
const checkTicWin = (board, player, boardSize) => {
  // Use the comprehensive transformation checking for tic
  // Base pattern: horizontal line of 3
  const basePattern = [[0, 0], [0, 1], [0, 2]];
  return checkPatternAllTransformations(board, basePattern, player, boardSize, true, 'tic'); // isLinePattern = true
};

/**
 * Specialized win check for skinny pattern (4-in-a-row)
 * @param {Array} board - The game board
 * @param {string} player - The player to check ('X' or 'O')
 * @param {number} boardSize - The size of the game board
 * @returns {Object|null} Object with winning positions if player has won, null otherwise
 */
const checkSkinnyWin = (board, player, boardSize) => {
  // Use the comprehensive transformation checking for skinny
  // Base pattern: horizontal line of 4
  const basePattern = [[0, 0], [0, 1], [0, 2], [0, 3]];
  return checkPatternAllTransformations(board, basePattern, player, boardSize, true, 'skinny'); // isLinePattern = true
};

export const patterns = [
  {
    id: 'tic',
    name: 'Tic',
    description: 'Three pieces in a row (horizontal or vertical only)',
    icon: '▢▢▢',
    minBoardSize: 3,
    checkFunction: (board, player, boardSize) => {
      // Specialized check for tic pattern to ensure all cases are covered
      return checkTicWin(board, player, boardSize);
    }
  },
  {
    id: 'skinny',
    name: 'Skinny',
    description: 'Line of 4 pieces (horizontal or vertical only)',
    icon: '▢\n▢\n▢\n▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Specialized check for skinny pattern to ensure all cases are covered
      return checkSkinnyWin(board, player, boardSize);
    }
  },
  {
    id: 'el',
    name: 'El',
    description: 'Three pieces in a right-angled L-shape',
    icon: '▢\n▢\n▢▢',
    minBoardSize: 3,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: L-shape pointing right and down (3 pieces)
      const basePattern = [[0, 0], [1, 0], [1, 1]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'el');
    }
  },
  {
    id: 'boxy',
    name: 'Boxy',
    description: '2x2 square formation (4 pieces)',
    icon: '▢▢\n▢▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: 2x2 square
      const basePattern = [[0, 0], [0, 1], [1, 0], [1, 1]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'boxy');
    }
  },
  {
    id: 'elly',
    name: 'Elly',
    description: 'L-shaped formation (4 pieces)',
    icon: '▢\n▢\n▢▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: L-shape pointing right and down
      const basePattern = [[0, 0], [1, 0], [2, 0], [2, 1]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'elly');
    }
  },
  {
    id: 'knobby',
    name: 'Knobby',
    description: 'T-shaped formation (4 pieces)',
    icon: '▢▢▢\n ▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: T-shape pointing down
      const basePattern = [[0, -1], [0, 0], [0, 1], [1, 0]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'knobby');
    }
  },
  {
    id: 'tippy',
    name: 'Tippy',
    description: 'Snake-like zigzag formation (4 pieces)',
    icon: '▢▢\n▢▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: horizontal snake (top-left to bottom-right zigzag)
      const basePattern = [[0, 0], [0, 1], [1, 1], [1, 2]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'tippy');
    }
  },
  {
    id: 'snaky',
    name: 'Snaky',
    description: 'Snake-like formation (6 pieces)',
    icon: '▢▢\n ▢▢▢▢',
    minBoardSize: 5,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: snake formation (2 pieces on top, 4 pieces on bottom, offset by 1)
      const basePattern = [[0, 0], [0, 1], [1, 1], [1, 2], [1, 3], [1, 4]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'snaky');
    }
  },
  {
    id: 'aspy',
    name: 'Ducky',
    description: 'Pentomino with 2+4 overlapping pieces',
    icon: '▢▢\n▢▢▢▢',
    minBoardSize: 5,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: 2 pieces on top, 4 pieces on bottom, with overlap (5 total)
      const basePattern = [[0, 0], [0, 1], [1, 1], [1, 2], [1, 3]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'aspy');
    }
  },
  {
    id: 'cross',
    name: 'Cross',
    description: 'Cross formation (5 pieces) - rotation invariant',
    icon: ' ▢ \n▢▢▢\n ▢ ',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: cross shape (rotation invariant)
      const basePattern = [[0, 1], [1, 0], [1, 1], [1, 2], [2, 1]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'cross');
    }
  },
  {
    id: 'tee',
    name: 'Tee',
    description: 'T-shaped formation (5 pieces) - four rotations',
    icon: '▢▢▢\n ▢ \n ▢ ',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: T-shape pointing down
      const basePattern = [[0, 0], [0, 1], [0, 2], [1, 1], [2, 1]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'tee');
    }
  },
    {
    id: 'f',
    name: 'F',
    description: 'F-shaped pentomino (5 pieces) - four rotations',
    icon: '  ▢\n▢  \n▢ ▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: F-shape (vertical stack with side squares)
      const basePattern = [[0, 1], [0, 2], [1, 1], [2, 0], [2, 1]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'f');
    }
  },
  {
    id: 'stair',
    name: 'Steppy',
    description: 'Staircase-shaped pentomino (5 pieces) - four rotations',
    icon: '▢\n▢▢\n ▢▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: Staircase-shape (x/xx/ xx)
      const basePattern = [[0, 0], [1, 0], [1, 1], [2, 1], [2, 2]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'stair');
    }
  },
  {
    id: 'p',
    name: 'P',
    description: 'P-shaped pentomino (5 pieces) - four rotations',
    icon: ' ▢▢\n▢▢ \n▢  ',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: P-shape ( xx/xx/ x)
      const basePattern = [[0, 1], [0, 2], [1, 0], [1, 1], [2, 1]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'p');
    }
  },
  {
    id: 'l',
    name: 'L',
    description: 'L-shaped pentomino (5 pieces) - four rotations',
    icon: '▢\n▢\n▢▢▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: L-shape
      const basePattern = [[0, 0], [1, 0], [2, 0], [2, 1], [2, 2]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'l');
    }
  },
  {
    id: 'y',
    name: 'Y',
    description: 'Y-shaped hexomino (6 pieces) - four rotations',
    icon: '▢   \n▢▢▢▢\n   ▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: Y-shape (xooo/xxxx/oooX)
      const basePattern = [[0, 0], [1, 0], [1, 1], [1, 2], [1, 3], [2, 3]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'y');
    }
  },
  {
    id: 'u',
    name: 'U',
    description: 'U-shaped pentomino (5 pieces) - multiple rotations',
    icon: ' ▢ ▢\n▢▢▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      const basePattern = [[0, 0], [0, 2], [1, 0], [1, 1], [1, 2]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'u');
    }
  },
  {
    id: 'c',
    name: 'C',
    description: 'C-shaped hexomino (6 pieces) - multiple rotations',
    icon: '▢▢▢▢\n▢  ▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      const basePattern = [[0, 0], [0, 1], [0, 2], [0, 3], [1, 0], [1, 3]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 'c');
    }
  },
  {
    id: 's',
    name: 'S',
    description: 'S-shaped hexomino (6 pieces) - multiple rotations',
    icon: '  ▢▢▢\n▢▢▢',
    minBoardSize: 4,
    checkFunction: (board, player, boardSize) => {
      // Base pattern: S-shape (two rows of three, top shifted right)
      const basePattern = [[0, 2], [0, 3], [0, 4], [1, 0], [1, 1], [1, 2]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, false, 's');
    }
  },
  {
    id: 'i',
    name: 'I',
    description: 'I-shaped pentomino (5 pieces) - two rotations',
    icon: '▢▢▢▢▢',
    minBoardSize: 5,
    checkFunction: (board, player, boardSize) => {
      const basePattern = [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4]];
      return checkPatternAllTransformations(board, basePattern, player, boardSize, true, 'i');
    }
  }
];

/**
 * Get a pattern by its ID
 * @param {string} patternId - The pattern identifier
 * @returns {Object|null} The pattern object or null if not found
 */
export const getPatternById = (patternId) => {
  return patterns.find(pattern => pattern.id === patternId) || null;
};

/**
 * Get all patterns that are compatible with a given board size
 * @param {number} boardSize - The size of the game board
 * @returns {Array} Array of compatible patterns
 */
export const getCompatiblePatterns = (boardSize) => {
  return patterns.filter(pattern => boardSize >= pattern.minBoardSize);
};

/**
 * Check if a player has won with a specific pattern
 * @param {Array} board - The game board
 * @param {string} patternId - The pattern identifier
 * @param {string} player - The player to check ('X' or 'O')
 * @param {number} boardSize - The size of the game board
 * @returns {Object|null} Object with winning positions if player has won, null otherwise
 */
export const checkWin = (board, patternId, player, boardSize) => {
  const pattern = getPatternById(patternId);
  if (!pattern) {
    console.warn(`Pattern "${patternId}" not found`);
    return null;
  }
  
  if (boardSize < pattern.minBoardSize) {
    console.warn(`Board size ${boardSize} is too small for pattern "${patternId}" (minimum: ${pattern.minBoardSize})`);
    return null;
  }
  
  return pattern.checkFunction(board, player, boardSize);
};

/**
 * Classify polyominoes according to their game theory properties
 * @param {string} patternId - The pattern identifier
 * @returns {string} Classification: 'basic-loser', 'loser', 'economical-winner', 'winner', or 'unknown'
 */
export const classifyPolyomino = (patternId) => {
  const classifications = {
    // Basic losers - can be blocked by simple strategies
    'p': 'basic-loser',
    'l': 'basic-loser',
    'y': 'basic-loser',
    'u': 'basic-loser',
    'c': 'basic-loser',
    's': 'basic-loser', // S
    'i': 'basic-loser',
    'boxy': 'basic-loser',
    'cross': 'basic-loser',
    'tee': 'basic-loser',
    'f': 'basic-loser', // F
    'stair': 'basic-loser',
    
    // Economical winners - require careful play but can be won
    'tic': 'economical-winner',
    'el': 'economical-winner',
    'elly': 'economical-winner',
    'knobby': 'economical-winner',
    
    // Winners - strong patterns that are hard to block
    'skinny': 'winner',
    'tippy': 'winner',
    'snaky': 'winner',
    'aspy': 'winner',
    
    // Unknown - patterns whose properties haven't been determined
    // (None currently identified)
  };
  
  return classifications[patternId] || 'unknown';
};

/**
 * Get all polyominoes of a specific classification
 * @param {string} classification - The classification to filter by
 * @returns {Array} Array of pattern IDs with that classification
 */
export const getPolyominoesByClassification = (classification) => {
  const allPatterns = [
    'tic', 'skinny', 'el', 'boxy', 'elly', 'knobby', 'tippy', 'snaky', 'aspy',
    'cross', 'tee', 'f', 'stair', 'p', 'l', 'y', 'u', 'c', 's', 'i'
  ];
  
  return allPatterns.filter(patternId => classifyPolyomino(patternId) === classification);
};

/**
 * Get a summary of all polyomino classifications
 * @returns {Object} Object with classification counts and lists
 */
export const getPolyominoClassificationSummary = () => {
  const allPatterns = [
    'tic', 'skinny', 'el', 'boxy', 'elly', 'knobby', 'tippy', 'snaky', 'aspy',
    'cross', 'tee', 'f', 'stair', 'p', 'l', 'y', 'u', 'c', 's', 'i'
  ];
  
  const summary = {
    'basic-loser': [],
    'loser': [],
    'economical-winner': [],
    'winner': [],
    'unknown': []
  };
  
  allPatterns.forEach(patternId => {
    const classification = classifyPolyomino(patternId);
    summary[classification].push(patternId);
  });
  
  return summary;
};

/**
 * Analysis function to count transformations for each pattern
 * This helps understand how many unique orientations each pattern has
 */
export const analyzePatternTransformations = () => {
  const patterns = [
    {
      name: 'triplet',
      basePattern: [[0, 0], [0, 1], [0, 2]],
      isLinePattern: true
    },
    {
      name: 'skinny',
      basePattern: [[0, 0], [0, 1], [0, 2], [0, 3]],
      isLinePattern: true
    },
    {
      name: 'boxy',
      basePattern: [[0, 0], [0, 1], [1, 0], [1, 1]],
      isLinePattern: false
    },
    {
      name: 'ellie',
      basePattern: [[0, 0], [1, 0], [2, 0], [2, 1]],
      isLinePattern: false
    },
    {
      name: 'knobby',
      basePattern: [[0, -1], [0, 0], [0, 1], [1, 0]],
      isLinePattern: false
    },
    {
      name: 'snaky',
      basePattern: [[0, 0], [0, 1], [1, 1], [1, 2]],
      isLinePattern: false
    }
  ];

  const results = {};
  
  for (const pattern of patterns) {
    const transformations = generateAllTransformations(pattern.basePattern, pattern.isLinePattern);
    results[pattern.name] = {
      count: transformations.length,
      transformations: transformations,
      description: pattern.isLinePattern ? 
        'Line pattern: Horizontal, Vertical - 2 orientations' :
        'Non-line pattern: All rotations and reflections'
    };
  }
  
  return results;
};

/**
 * Calculate a heatmap showing how many ways each square can be part of the target pattern
 * @param {Array} board - 2D array representing the current game board
 * @param {string} patternId - The target pattern identifier
 * @param {number} boardSize - Size of the board
 * @param {string} player - Player to check for ('X' or 'O')
 * @returns {Array} 2D array where heatmap[i][j] = number of ways pattern can include square (i,j)
 */
export const calculatePatternHeatmap = (board, patternId, boardSize, player = 'X') => {
  // Validate inputs
  if (!board || !Array.isArray(board) || board.length === 0) {
    console.warn('Invalid board provided to calculatePatternHeatmap');
    return Array(boardSize).fill(null).map(() => Array(boardSize).fill(0));
  }

  if (!patternId || typeof patternId !== 'string') {
    console.warn('Invalid patternId provided to calculatePatternHeatmap');
    return Array(boardSize).fill(null).map(() => Array(boardSize).fill(0));
  }

  const pattern = getPatternById(patternId);
  if (!pattern) {
    console.warn(`Pattern "${patternId}" not found`);
    return Array(boardSize).fill(null).map(() => Array(boardSize).fill(0));
  }

  // Get the base pattern positions
  const basePattern = pattern.checkFunction ? 
    // Extract base pattern from the pattern's checkFunction
    (() => {
      switch (patternId) {
        case 'tic': return [[0, 0], [0, 1], [0, 2]];
        case 'skinny': return [[0, 0], [0, 1], [0, 2], [0, 3]];
        case 'el': return [[0, 0], [1, 0], [1, 1]];
        case 'boxy': return [[0, 0], [0, 1], [1, 0], [1, 1]];
        case 'elly': return [[0, 0], [1, 0], [2, 0], [2, 1]];
        case 'knobby': return [[0, -1], [0, 0], [0, 1], [1, 0]];
        case 'tippy': return [[0, 0], [0, 1], [1, 1], [1, 2]];
        case 'snaky': return [[0, 0], [0, 1], [1, 1], [1, 2], [1, 3], [1, 4]];
        case 'aspy': return [[0, 0], [0, 1], [1, 1], [1, 2], [1, 3]];
        case 'cross': return [[0, 1], [1, 0], [1, 1], [1, 2], [2, 1]];
        case 'tee': return [[0, 0], [0, 1], [0, 2], [1, 1], [2, 1]];
        case 'f': return [[0, 1], [0, 2], [1, 1], [2, 0], [2, 1]];
        case 'stair': return [[0, 0], [1, 0], [1, 1], [2, 1], [2, 2]];
        case 'p': return [[0, 1], [0, 2], [1, 0], [1, 1], [2, 1]];
        case 'l': return [[0, 0], [1, 0], [2, 0], [2, 1], [2, 2]];
        case 'y': return [[0, 0], [1, 0], [1, 1], [1, 2], [1, 3], [2, 3]];
        case 'u': return [[0, 0], [0, 2], [1, 0], [1, 1], [1, 2]];
        case 'c': return [[0, 0], [0, 1], [0, 2], [0, 3], [1, 0], [1, 3]];
        case 's': return [[0, 2], [0, 3], [0, 4], [1, 0], [1, 1], [1, 2]];
        case 'i': return [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4]];
        default: return [[0, 0]];
      }
    })() : [[0, 0]];

  // Generate all unique transformations of the pattern
  const transformations = generateHeatmapTransformations(basePattern);
  
  // Initialize heatmap
  const heatmap = Array(boardSize).fill(null).map(() => Array(boardSize).fill(0));

  // For each square on the board
  for (let targetRow = 0; targetRow < boardSize; targetRow++) {
    for (let targetCol = 0; targetCol < boardSize; targetCol++) {
      let count = 0;

      // For each transformation of the pattern
      for (const transformedPattern of transformations) {
        // Try to place the pattern so that it covers the target square
        const validPlacements = findValidPlacementsForSquare(
          board, 
          transformedPattern, 
          targetRow, 
          targetCol, 
          boardSize, 
          player
        );
        count += validPlacements.length;
      }

      heatmap[targetRow][targetCol] = count;
    }
  }

  return heatmap;
};

/**
 * Generate all unique rotations and reflections of a pattern for heatmap calculation
 * @param {Array} basePattern - Array of [row, col] positions
 * @returns {Array} Array of transformed patterns
 */
const generateHeatmapTransformations = (basePattern) => {
  const transformations = [];
  const seen = new Set();

  // Generate all 8 transformations (4 rotations × 2 reflections)
  for (let rotation = 0; rotation < 4; rotation++) {
    for (let reflection = 0; reflection < 2; reflection++) {
      let transformed = [...basePattern];

      // Apply rotation
      for (let i = 0; i < rotation; i++) {
        transformed = transformed.map(([r, c]) => [-c, r]);
      }

      // Apply reflection (horizontal flip)
      if (reflection === 1) {
        transformed = transformed.map(([r, c]) => [r, -c]);
      }

      // Normalize the pattern (shift to origin)
      const minRow = Math.min(...transformed.map(([r, c]) => r));
      const minCol = Math.min(...transformed.map(([r, c]) => c));
      transformed = transformed.map(([r, c]) => [r - minRow, c - minCol]);

      // Create a unique key for this transformation
      const key = transformed.map(([r, c]) => `${r},${c}`).sort().join('|');
      
      if (!seen.has(key)) {
        seen.add(key);
        transformations.push(transformed);
      }
    }
  }

  return transformations;
};

/**
 * Find all valid placements of a pattern that cover a specific square
 * @param {Array} board - Current game board
 * @param {Array} pattern - Transformed pattern positions
 * @param {number} targetRow - Target square row
 * @param {number} targetCol - Target square column
 * @param {number} boardSize - Board size
 * @param {string} player - Player to check for
 * @returns {Array} Array of valid placement positions
 */
const findValidPlacementsForSquare = (board, pattern, targetRow, targetCol, boardSize, player) => {
  const validPlacements = [];

  // Validate inputs
  if (!pattern || !Array.isArray(pattern) || pattern.length === 0) {
    return validPlacements;
  }

  if (!board || !Array.isArray(board) || board.length === 0) {
    return validPlacements;
  }

  // Find which position in the pattern corresponds to the target square
  for (let patternIndex = 0; patternIndex < pattern.length; patternIndex++) {
    const patternPos = pattern[patternIndex];
    if (!patternPos || !Array.isArray(patternPos) || patternPos.length < 2) {
      continue; // Skip invalid pattern positions
    }
    
    const [patternRow, patternCol] = patternPos;
    
    // Calculate the offset needed to place this pattern position at the target square
    const offsetRow = targetRow - patternRow;
    const offsetCol = targetCol - patternCol;

    // Check if this placement is valid
    let isValid = true;
    for (const [r, c] of pattern) {
      const boardRow = r + offsetRow;
      const boardCol = c + offsetCol;

      // Check bounds
      if (boardRow < 0 || boardRow >= boardSize || boardCol < 0 || boardCol >= boardSize) {
        isValid = false;
        break;
      }

      // Check if square is available (empty or contains player's piece)
      const cellValue = board[boardRow][boardCol];
      if (cellValue !== null && cellValue !== player) {
        isValid = false;
        break;
      }
    }

    if (isValid) {
      validPlacements.push({ offsetRow, offsetCol, patternIndex });
    }
  }

  return validPlacements;
};
